import React, { useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Checkbox from '@mui/material/Checkbox';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import RefreshIcon from '@mui/icons-material/Refresh';
import GetAppIcon from '@mui/icons-material/GetApp';
import PublishIcon from '@mui/icons-material/Publish';
import AddIcon from '@mui/icons-material/Add';
import TuneIcon from '@mui/icons-material/Tune';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import DeleteIcon from '@mui/icons-material/Delete';
import LzMCrud from '@lz/models/crud/Crud';
import LzMCrudListingColumn from '@lz/models/crud/ListingColumn';
import LzMCrudListingActionIcon from '@lz/models/crud/ListingActionIcon';
import LzMBase, { LzTBase } from '@lz/models/commons/Base';
import LzXToolbar, { LzTToolbarItem } from '../commons/Toolbar/Toolbar';
import LzXFiltersDialog from '../commons/FiltersDialog';
import LzCEntity from '@lz/modules/Entity';
import useLzFApiCall from '@lz/services/Api';
import { lzFDeepVarSet } from '@lz/services/Type';
import { useLzEffectAfterMount, useLzOnMount } from '@lz/services/Hooks';
import { lzFLogInfo, lzFShowSuccessMessage, useLzFUrlQueryParams } from '@lz/services/Utils';
import lzSCommonsFullscreenLogMessageAtom from '@lz-atoms/Commons/fullscreenLogMessage';
import { LzTOpenObject, LzTSortDirections } from '@lz/typings/types';
import LzXSelectField from '../forms/fields/SelectField/SelectField';
import { lzFPluralize } from '@lz/services/String';
import LzMOption from '@lz/models/commons/Option';
import LzMCrudListGroupActionIcon from '@lz/models/crud/ListingGroupActionIcon';
import LzXImportParamsDialog from '../commons/ImportParamsDialog';

export interface LzPCrudListing {
    crud: LzMCrud;
    modelRef: LzTBase;
    contentAboveToolbar?: any;
    contentAboveFilters?: any;
    contentAboveListing?: any;
    listingCallExtraParams?: LzTOpenObject;
    columnDisplayExtraParams?: LzTOpenObject;
    recordsPerPage?: number;
    getInitFilters?: (locState?: LzTOpenObject, queryStringVars?: LzTOpenObject) => any;
    filtersInput?: (
        data: LzTOpenObject,
        handleInputChanage: (name: string, value: any) => void,
        handleInputChanages?: (changes: LzTOpenObject) => void,
    ) => React.ReactNode;
    showFiltersInline?: boolean;
    sortKey?: string;
    sortDir?: LzTSortDirections;
    showDeleted?: boolean;
    onActionIconClick?: (key: string, record: LzMBase) => boolean;
    onGroupActionIconClick?: (key: string, confirmation?: string) => boolean;
    onLoadSuccess?: (data: any) => void;
    children?: React.ReactNode;
}

const LzXCrudListing = forwardRef((props: LzPCrudListing, ref) => {
    // Initialize a flag to be able to skip useEffect() dependency based loading that makes an extra call for no reason
    const skipInitialLoadBubbling = useRef(true);

    // To refresh component on route change, which is not working otherwise for some weird reason
    const location = useLocation();
    const lzFUrlQueryParams = useLzFUrlQueryParams();
    const locState: LzTOpenObject = location.state as LzTOpenObject;

    // console.log('LOCATION', location);

    if (typeof props.crud === 'undefined' || !props.crud || '' === props.crud.ident) {
        return (
            <Alert variant="filled" severity="error">
                CRUD configurations is missing!
            </Alert>
        );
    }

    if (typeof props.modelRef === 'undefined' || !props.modelRef) {
        return (
            <Alert variant="filled" severity="error">
                Model definition is missing!
            </Alert>
        );
    }

    const getInitFilters = (locState?: LzTOpenObject, queryStringVars?: LzTOpenObject) => {
        if (typeof props.getInitFilters !== 'undefined') {
            return props.getInitFilters(locState, queryStringVars);
        }

        return {};
    };

    const getInitImportParams = () => {
        if (typeof props.crud.importGetInitParams !== 'undefined') {
            return props.crud.importGetInitParams();
        }

        return {};
    };

    const [loadedStats, setLoadedStats] = useState<LzTOpenObject | null>(null);
    const [loadedRecords, setLoadedRecords] = useState<LzMBase[] | null>(null);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [recordsPerPage, setRecordsPerPage] = useState(props.recordsPerPage || 10);
    const [filters, setFilters] = useState<LzTOpenObject>(getInitFilters(props.crud.getRouteParams(locState), lzFUrlQueryParams()));
    const [showFilters, setShowFilters] = useState(false);
    const [importParams, setImportParams] = useState<LzTOpenObject>(getInitImportParams());
    const [showImportParams, setShowImportParams] = useState(false);
    const [sortKey, setSortKey] = useState(props.sortKey || '');
    const [sortDir, setSortDir] = useState(props.sortDir || 'asc');
    const [checkedIds, setCheckedIds] = useState<number[]>([]);
    const [showDeleted, setShowDeleted] = useState(!!props.showDeleted);
    const [actionIconComponent, setActionIconComponent] = useState(undefined);

    // Collect necessary data for parent field
    const parentFieldSpecs = props.crud.parentFieldKey ? LzCEntity.getParentFieldSpecsFromKey(props.crud.parentFieldKey) : null;
    const parentFieldIdVarName = parentFieldSpecs ? (parentFieldSpecs.idFieldName as keyof typeof location.state) : null;
    const parentFieldNameVarName = parentFieldSpecs ? (parentFieldSpecs.nameFieldName as keyof typeof location.state) : null;
    const parentFieldIdFromLocState = parentFieldIdVarName ? locState?.[parentFieldIdVarName] || 0 : 0;
    const parentFieldNameFromLocState = parentFieldNameVarName ? locState?.[parentFieldNameVarName] : '';
    const parentFieldLabel = parentFieldSpecs?.label || '';

    const setCommonsFullscreenLogMessageState = useSetAtom(lzSCommonsFullscreenLogMessageAtom);

    const lzApiCall = useLzFApiCall();

    const loadRecords = async () => {
        // Make params to send
        const params: LzTOpenObject = {
            _sort_order: typeof sortKey === 'undefined' || !sortKey || '' === sortKey ? '' : sortKey + ' ' + sortDir,
            _page_index: currentPage + 1,
            _page_size: recordsPerPage,
            _show_deleted: showDeleted,
        };

        // Add filters to params if any
        for (const key in filters) {
            params[key] = filters[key];
        }

        // Add listingCallExtraParams to params if any
        if (props.listingCallExtraParams) {
            for (const key in props.listingCallExtraParams) {
                params[key] = props.listingCallExtraParams[key];
            }
        }

        // Add the parent field filter if it is set
        if (parentFieldSpecs) {
            if (parentFieldIdFromLocState && typeof params[parentFieldSpecs.idFieldName] === 'undefined') {
                params[parentFieldSpecs.idFieldName] = parentFieldIdFromLocState;
            }
        }

        // Make the api call and get the data
        const data = await lzApiCall(props.crud.ident, params);

        // Load data if call was successful
        if (data && data.data) {
            // If the changes will not cause bubbling then reset the flag
            if (
                sortKey === data.sort_key &&
                sortDir === data.sort_dir
                // TODO: Add any more if required
            ) {
                skipInitialLoadBubbling.current = false;
            }

            if (data.stats) {
                setLoadedStats(data.stats);
            }
            setLoadedRecords(LzMBase.parseArray(data.data, props.modelRef));
            setTotalRecords(+data.total);
            setCurrentPage(+data.current_page - 1);
            setRecordsPerPage(+data.per_page);
            setSortKey(data.sort_key);
            setSortDir(data.sort_dir);
            setCheckedIds([]);

            if (props.onLoadSuccess) {
                props.onLoadSuccess(data);
            }
        }
    };

    const loadNodeRecords = async (dexPath?: any, parentId?: number) => {
        const parentIdVal = parentId || 0;

        // console.log('LOADING-TREE-NODE', dexPath, parentId, parentIdVal);

        // Make params to send
        const params: LzTOpenObject = {
            parent_id: parentIdVal,
            _show_deleted: showDeleted,
        };

        // Add filters to params if any
        for (const key in filters) {
            params[key] = filters[key];
        }

        // Make the api call and get the data
        const data = await lzApiCall(props.crud.ident, params);

        // Load data if call was successful
        if (data) {
            let updated = loadedRecords?.slice() || [];
            if (parentIdVal > 0) {
                lzFDeepVarSet(updated, dexPath, LzMBase.parseArray(data, props.modelRef));
            } else {
                updated = LzMBase.parseArray(data, props.modelRef);
            }

            // We know that the changes will not cause bubbling for tree type listing so reset the flag
            skipInitialLoadBubbling.current = false;

            if (data.stats) {
                setLoadedStats(data.stats);
            }
            setLoadedRecords(updated);
            setCheckedIds([]);
        }
    };

    const loadListing = async () => {
        console.log('loadListing() called...', props.crud.ident);
        if (props.crud.isTreeBased) {
            await loadNodeRecords();
        } else {
            await loadRecords();
        }
    };

    useImperativeHandle(ref, () => ({
        loadTheListing: () => {
            loadListing();
        },
    }));

    /**
     * Load listing on component mount.
     */
    useLzOnMount(() => {
        console.log('useLzOnMount() called...', props.crud.ident);
        loadListing();
    });

    /**
     * Load listing on change of dependencies.
     */
    useLzEffectAfterMount(() => {
        // If this was called due to inital load bubbling, then skip it and reset the flag so calls after that are executed
        if (skipInitialLoadBubbling.current) {
            skipInitialLoadBubbling.current = false;
            return;
        }

        loadListing();
    }, [/* location,*/ sortKey, sortDir, currentPage, recordsPerPage, showDeleted]);
    // 15-Jan-2024: Commented because it calls loadListing() and that conflicts with loadTheListing() which is
    //              called by a child from the app, this results in double listing calls, where 1 call is correct
    //              while the other is a kind of leftover. Check AFAQ > Params > LocationIndexScreen for details.

    const handleFiltersInput = (name: string, value: any, extras?: any) => {
        setFilters(Object.assign({}, filters, { [name]: value }, extras || {}));
    };

    const handleFiltersMultipleChanges = (changes: LzTOpenObject) => {
        setFilters(Object.assign({}, filters, changes));
    };

    const resetFilters = () => {
        setFilters(getInitFilters(props.crud.getRouteParams(locState), lzFUrlQueryParams()));
    };

    const handleImportParamsInput = (name: string, value: any) => {
        setImportParams(Object.assign({}, importParams, { [name]: value }));
    };

    const resetImportParams = () => {
        setImportParams(getInitImportParams());
    };

    const onSortChange = (key: string, dir: LzTSortDirections) => {
        if (props.crud.listingColumns[key].sortable) {
            setSortKey(key);
            setSortDir(dir);
        }
    };

    const onPageChange = (pageNo: number) => {
        setCurrentPage(pageNo);
    };

    const onRecordsPerPageChange = (recordsPerPage: number) => {
        setCurrentPage(0);
        setRecordsPerPage(recordsPerPage);
    };

    const exportCsv = async () => {
        // Build params
        const params: LzTOpenObject = {
            _exportCsv: 1,
        };

        // Add filters to params if any
        for (const key in filters) {
            params[key] = filters[key];
        }

        // Call api
        const data = await lzApiCall(props.crud.ident, params);

        // Show log info
        lzFLogInfo('Export CSV Result', data);
    };

    const importCsv = async (file: File) => {
        // Make the api call and get the data
        const data = await lzApiCall(
            props.crud.ident,
            {
                _importCsv: 1,
                _csvFile: file,
                _importParams: importParams,
            },
            'post',
        );

        lzFLogInfo('Import CSV Result', data);

        // Load data if call was successful
        if (data) {
            setCommonsFullscreenLogMessageState(data);
        }
    };

    const handleUpdateRecord = async (record: any, onlyLocal?: boolean) => {
        if (onlyLocal) {
            const updated = loadedRecords?.slice() || [];
            const dex = updated.findIndex((rec) => record.id === rec.id);
            if (dex >= 0) {
                updated[dex] = record;
                setLoadedRecords(updated);
            }
        } else {
            const data = await lzApiCall(props.crud.ident + '/' + record.id, record, 'put');
            if (data) {
                lzFShowSuccessMessage('Record updated successfully!');
                loadListing();
            }
        }
    };

    // TODO: Check why this is unused
    const handleShowDeletedChange = (val: boolean) => {
        setShowDeleted(val);
    };

    const changeStatus = async (record: LzMBase) => {
        // Make the api call
        const data = await lzApiCall(
            props.crud.ident + '/' + record.id,
            {
                _action: 'active',
                is_active: !record.is_active,
            },
            'patch',
        );

        // Load data if call was successful
        if (data) {
            loadListing();
        }
    };

    const deleteRecord = async (id: number, confirmed?: boolean) => {
        if (!confirmed) {
            if (!window.confirm('Are you sure you want to delete this record?')) {
                return;
            }
        }

        // Make the api call
        const data = await lzApiCall(props.crud.ident + '/' + id, null, 'delete');

        // Load data if call was successful
        if (data) {
            loadListing();
        }
    };

    const deletedRecordAction = async (id: number, action: string, confirmed?: boolean) => {
        if (!confirmed) {
            if (!window.confirm('Are you sure you want to ' + action + ' this record?')) {
                return;
            }
        }

        // Make the api call
        const data = await lzApiCall(props.crud.ident + '/' + id, { _action: action }, 'delete');

        // Load data if call was successful
        if (data) {
            loadListing();
        }
    };

    const navigate = useNavigate();

    const importFileRef = React.createRef<HTMLInputElement>();

    const handleActionIconClickDefault = (key: string, record: LzMBase) => {
        if ('status' === key) {
            changeStatus(record);
        } else if ('delete' === key) {
            deleteRecord(record.id);
        } else if ('deleted_restore' === key) {
            deletedRecordAction(record.id, 'restore');
        } else if ('deleted_purge' === key) {
            deletedRecordAction(record.id, 'purge');
        } else {
            console.log('TODO: Handle Action Icon Event', key, record.id);
        }
    };

    const handleActionIconClick = (key: string, actIcon: LzMCrudListingActionIcon, record: LzMBase) => {
        if (actIcon.route !== '') {
            navigate(actIcon.route.replace('{baseRoute}', props.crud.baseRoute).replace('{recordId}', '' + record.id), {
                state: {
                    ...(typeof actIcon.getRouteParams === 'undefined' ? {} : actIcon.getRouteParams(record)),
                    ...props.crud.getRouteParams(locState),
                },
            });

            return;
        }

        if (typeof actIcon.showComponent !== 'undefined') {
            setActionIconComponent(
                actIcon.showComponent(
                    record,
                    () => setActionIconComponent(undefined),
                    () => loadListing(),
                ),
            );

            return;
        }

        // If a custom action icon click handler is defined
        if (props.onActionIconClick) {
            // Then call it and if it handled the event successfully then skip (else the default handler will be called)
            if (props.onActionIconClick(key, record)) {
                return;
            }
        }

        handleActionIconClickDefault(key, record);
    };

    const handleGroupActionIconClickDefault = async (key: string, confirmation?: string) => {
        // If confirmation msg is provided then first confirm
        if (confirmation && confirmation !== '') {
            if (!window.confirm(confirmation)) {
                return;
            }
        }

        // Make the api call
        const data = await lzApiCall(
            props.crud.ident + '/0',
            {
                _action: key,
                ids: checkedIds,
            },
            'patch',
        );

        // Load data if call was successful
        if (data) {
            loadListing();
        }
    };

    const handleGroupActionIconClick = (key: string, actIcon: LzMCrudListGroupActionIcon) => {
        // If a custom group action icon click handler is defined
        if (props.onGroupActionIconClick) {
            // Then call it and if it handled the event successfully then skip (else the default handler will be called)
            if (props.onGroupActionIconClick(key, actIcon.confirmation)) {
                return;
            }
        }

        handleGroupActionIconClickDefault(key, actIcon.confirmation);
    };

    const handleParentFieldChange = (value: string | number, option?: LzMOption | null) => {
        // Build the new state from the current state
        const newState = props.crud.getRouteParams(locState);

        // Replace the parent id and name values in the new state
        if (parentFieldIdVarName && parentFieldNameVarName) {
            newState[parentFieldIdVarName] = value;
            newState[parentFieldNameVarName] = option?.text || '';
        }

        // console.log('PC', value, location.pathname, locState, props.crud.getRouteParams(locState), 'NS:', newState);
        navigate(location.pathname, { state: newState });
    };

    const showContentAbove = (content: any) => {
        if (typeof content === 'function') {
            return content({
                stats: loadedStats,
                records: loadedRecords,
            });
        }

        return content;
    };

    const showImportFileDialog = () => {
        if (importFileRef.current) {
            importFileRef.current.click();
        }
    };

    const handleImportCsvClick = () => {
        // If input params for import are defined in crud, then show them
        if (typeof props.crud.importGetInitParams !== 'undefined' && typeof props.crud.importGetParamsInput !== 'undefined') {
            setShowImportParams(true);
            return;
        }

        // Else directly hit the file load dialog
        showImportFileDialog();
    };

    const applyImportParams = () => {
        showImportFileDialog();
        setShowImportParams(false);
    };

    const clearImportFileInput = () => {
        if (importFileRef.current) {
            importFileRef.current.value = '';
        }
    };

    const onImportFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.stopPropagation();
        event.preventDefault();

        const files = event.target.files;
        if (typeof files === 'undefined' || !files || files.length < 1) {
            return;
        }

        const file = files[0];
        // console.log('File to Import:', file);
        importCsv(file);
        clearImportFileInput();
    };

    const getToolbarItems = (totalChecked: number) => {
        const out: LzTToolbarItem[] = [];

        if (totalChecked > 0) {
            for (const key in props.crud.listingGroupActionIcons) {
                const ai = props.crud.listingGroupActionIcons[key];
                out.push({
                    route: ai.route,
                    label: ai.title,
                    icon: ai.icon,
                    color: ai.color,
                    onClick: () => handleGroupActionIconClick(key, ai),
                    sortOrder: ai.sortOrder,
                });
            }
        } else {
            out.push({ label: 'Refresh', icon: <RefreshIcon />, color: 'success', onClick: loadListing, sortOrder: 5 });
            if (props.crud.showAdd) {
                out.push({
                    label: 'Create ' + props.crud.label,
                    icon: <AddIcon />,
                    color: 'info',
                    route: props.crud.baseRoute + '/add',
                    routeParams: props.crud.getRouteParams(Object.assign({}, locState, filters)),
                    sortOrder: 30,
                });
            }
            if (props.crud.showExport) {
                out.push({ label: 'Export CSV', icon: <GetAppIcon />, color: 'info', onClick: exportCsv, sortOrder: 10 });
            }
            if (props.crud.showImport) {
                out.push({ label: 'Import CSV', icon: <PublishIcon />, color: 'info', onClick: () => handleImportCsvClick(), sortOrder: 20 });
            }
            if (typeof props.filtersInput !== 'undefined' && typeof props.filtersInput({}, () => { }) !== 'undefined' && !props.showFiltersInline) {
                out.push({ label: 'Show Filters', icon: <TuneIcon />, color: 'warning', onClick: () => setShowFilters(!showFilters), sortOrder: 40 });
            }
            for (const key in props.crud.listingGroupActionIcons) {
                const ai = props.crud.listingGroupActionIcons[key];
                if (ai.checksNotRequiredToShow) {
                    out.push({
                        route: ai.route,
                        label: ai.title,
                        icon: ai.icon,
                        color: ai.color,
                        onClick: () => handleGroupActionIconClick(key, ai),
                        sortOrder: ai.sortOrder,
                    });
                }
            }
            if (props.crud.showDeletedList) {
                if (showDeleted) {
                    out.push({
                        label: 'Hide Deleted Records ',
                        icon: <DeleteForeverIcon />,
                        color: 'warning',
                        onClick: () => setShowDeleted(false),
                        sortOrder: 100,
                    });
                } else {
                    out.push({
                        label: 'Show Deleted Records',
                        icon: <DeleteIcon />,
                        color: 'warning',
                        onClick: () => setShowDeleted(true),
                        sortOrder: 110,
                    });
                }
            }
        }

        return out;
    };

    const getToolbarOtherStack = (totalChecked: number) => {
        if (totalChecked < 1) {
            return (
                <React.Fragment>
                    {typeof props.crud.parentFieldRoute !== 'undefined' && props.crud.parentFieldRoute !== '' && (
                        <React.Fragment>
                            <IconButton onClick={() => navigate(props.crud.parentFieldRoute || '')}>
                                <ArrowBackIcon />
                            </IconButton>
                            <Typography variant="button">
                                {parentFieldLabel}:&nbsp;
                                {props.crud.parentFieldShowFilterOnListing ? (
                                    <LzXSelectField
                                        size="tiny"
                                        variant="outlined"
                                        name={'parent_id'}
                                        value={parentFieldIdFromLocState}
                                        firstOptionText={'All ' + lzFPluralize(parentFieldLabel) + '...'}
                                        refTableName={props.crud.parentFieldKey}
                                        onChange={(nam, val, obj) => handleParentFieldChange(val, obj)}
                                    />
                                ) : (
                                    <React.Fragment>{parentFieldNameFromLocState}</React.Fragment>
                                )}
                            </Typography>
                        </React.Fragment>
                    )}
                </React.Fragment>
            );
        }

        return (
            <Typography color="inherit" variant="subtitle1">
                &nbsp; {totalChecked} selected
                <Tooltip title="Clear Selections">
                    <IconButton onClick={() => setCheckedIds([])}>
                        <ClearAllIcon />
                    </IconButton>
                </Tooltip>
            </Typography>
        );
    };

    const handleCheckAllClick = (checked: boolean) => {
        if (checked) {
            setCheckedIds(loadedRecords?.map((r) => r.id) || []);
        } else {
            setCheckedIds([]);
        }
    };

    const handleCheckClick = (checked: boolean, id: number) => {
        const updated = checkedIds.slice(); // get a copy
        const dex = updated.indexOf(id); // get index of the id

        // If it was supposed to be checked but is not
        if (checked && dex < 0) {
            updated.push(id);
            setCheckedIds(updated);
            return;
        }

        // If it was not supposed to be checked but it is
        if (!checked && dex >= 0) {
            updated.splice(dex, 1);
            setCheckedIds(updated);
        }
    };

    const applyFilters = () => {
        loadListing();
        setShowFilters(false);
    };

    if (!loadedRecords) {
        return <CircularProgress />;
    }

    const totalVisibleRows = loadedRecords.length;
    const totalChecked = checkedIds.length;

    const lstCols = props.crud.listingColumns;
    const lstColsKeys = Object.keys(lstCols);

    const lstActIcons = props.crud.listingActionIcons;
    const lstActIconsKeys = Object.keys(lstActIcons);

    // If parent field exists but the id is not selected for it (i.e. showing all of them), then show the parent column
    const showParentFieldColumn = parentFieldSpecs && parentFieldNameVarName && 0 === parentFieldIdFromLocState;

    return (
        <React.Fragment>
            {props.children ? (
                props.children
            ) : (
                <Paper elevation={3} style={{ width: '100%' }}>
                    {actionIconComponent}

                    {showContentAbove(props.contentAboveToolbar)}

                    <LzXToolbar
                        icons
                        highlighted={totalChecked > 0}
                        items={getToolbarItems(totalChecked)}
                        otherStack={getToolbarOtherStack(totalChecked)}
                    />

                    {showContentAbove(props.contentAboveFilters)}

                    {typeof props.filtersInput !== 'undefined' && (
                        <React.Fragment>
                            {props.showFiltersInline ? (
                                <div className="lz-listing-filters-inline">
                                    <Grid container spacing={1}>
                                        <Grid>
                                            {props.filtersInput(filters, handleFiltersInput, handleFiltersMultipleChanges)}
                                        </Grid>
                                        <Grid style={{ paddingTop: 12 }}>
                                            <Button onClick={applyFilters} size="small" color="primary" variant="contained">
                                                Apply
                                            </Button>
                                            <Button onClick={resetFilters} size="small">
                                                Reset
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </div>
                            ) : (
                                <LzXFiltersDialog
                                    show={showFilters}
                                    onClose={() => setShowFilters(false)}
                                    onReset={() => resetFilters()}
                                    onApply={() => applyFilters()}
                                >
                                    {props.filtersInput(filters, handleFiltersInput, handleFiltersMultipleChanges)}
                                </LzXFiltersDialog>
                            )}
                        </React.Fragment>
                    )}

                    {typeof props.crud.importGetParamsInput !== 'undefined' && (
                        <LzXImportParamsDialog
                            show={showImportParams}
                            onClose={() => setShowImportParams(false)}
                            onReset={() => resetImportParams()}
                            onApply={() => applyImportParams()}
                        >
                            {props.crud.importGetParamsInput(importParams, handleImportParamsInput)}
                        </LzXImportParamsDialog>
                    )}

                    {props.crud.showImport && (
                        <input
                            ref={importFileRef}
                            type="file"
                            style={{ display: 'none' }}
                            onChange={(event) => onImportFileChange(event)}
                            accept=".csv"
                        />
                    )}

                    {showContentAbove(props.contentAboveListing)}

                    {loadedRecords.length < 1 ? (
                        <Alert color="warning">No records found!</Alert>
                    ) : (
                        <React.Fragment>
                            <TableContainer style={{ maxHeight: '70vh' }}>
                                <Table stickyHeader className="lz-crud-listing-table">
                                    <TableHead>
                                        <TableRow>
                                            {props.crud.hasGroupActions && (
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        indeterminate={totalChecked > 0 && totalChecked < totalVisibleRows}
                                                        checked={totalVisibleRows > 0 && totalChecked === totalVisibleRows}
                                                        onChange={(ev) => handleCheckAllClick(ev.target.checked)}
                                                    />
                                                </TableCell>
                                            )}

                                            {showParentFieldColumn && (
                                                <TableCell align="left" className="lz-list-head">
                                                    {parentFieldLabel}
                                                </TableCell>
                                            )}

                                            {lstColsKeys.map((colKey, colDex) => {
                                                const lstCol = new LzMCrudListingColumn(lstCols[colKey]);

                                                const styles: LzTOpenObject = {};
                                                if (lstCol.noWrap) {
                                                    styles.whiteSpace = 'nowrap';
                                                }

                                                return (
                                                    <TableCell
                                                        key={'lc-' + colDex}
                                                        align={lstCol.alignment}
                                                        sortDirection={props.sortKey === colKey ? props.sortDir : false}
                                                        className="lz-list-head"
                                                        style={styles}
                                                    >
                                                        {lstCol.sortable ? (
                                                            <TableSortLabel
                                                                active={props.sortKey === colKey}
                                                                direction={props.sortDir}
                                                                onClick={() =>
                                                                    onSortChange(
                                                                        colKey,
                                                                        props.sortKey === colKey
                                                                            ? 'desc' === props.sortDir
                                                                                ? 'asc'
                                                                                : 'desc'
                                                                            : 'asc',
                                                                    )
                                                                }
                                                            >
                                                                {lstCol.icon && <React.Fragment>{lstCol.icon}</React.Fragment>}
                                                                {lstCol.label}
                                                            </TableSortLabel>
                                                        ) : (
                                                            <React.Fragment>
                                                                {lstCol.icon && <React.Fragment>{lstCol.icon}</React.Fragment>}
                                                                {lstCol.label}
                                                            </React.Fragment>
                                                        )}
                                                    </TableCell>
                                                );
                                            })}

                                            {lstActIconsKeys.length > 0 && (
                                                <TableCell align="center" style={{ width: 1 }}>
                                                    Actions
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {loadedRecords.map((record, trDex) => {
                                            const isChecked = checkedIds.indexOf(record.id) >= 0;

                                            return (
                                                <TableRow hover selected={isChecked} key={'trKey-' + trDex}>
                                                    {props.crud.hasGroupActions && (
                                                        <TableCell padding="checkbox">
                                                            <Checkbox
                                                                checked={isChecked}
                                                                onChange={(ev) => handleCheckClick(ev.target.checked, record.id)}
                                                            />
                                                        </TableCell>
                                                    )}

                                                    {showParentFieldColumn && (
                                                        <TableCell align="left">{record.getAttribute(parentFieldNameVarName)}</TableCell>
                                                    )}

                                                    {lstColsKeys.map((colKey, colDex) => {
                                                        const lstCol = new LzMCrudListingColumn(lstCols[colKey]);

                                                        const styles: LzTOpenObject = {};
                                                        if (lstCol.noWrap) {
                                                            styles.whiteSpace = 'nowrap';
                                                        }

                                                        return (
                                                            <TableCell
                                                                key={'tdKey-' + trDex + '-' + colDex}
                                                                align={lstCol.alignment}
                                                                className={'' + (lstCol.isMedia ? ' lz-crud-media-td' : '')}
                                                                style={styles}
                                                            >
                                                                {lstCol.displayFunction(
                                                                    record.getAttribute(colKey),
                                                                    record,
                                                                    props.columnDisplayExtraParams,
                                                                    handleUpdateRecord,
                                                                )}
                                                            </TableCell>
                                                        );
                                                    })}

                                                    {lstActIconsKeys.length > 0 && (
                                                        <TableCell padding="none" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                            {lstActIconsKeys.map((actionIconKey, actIconDex) => {
                                                                const actIcon = new LzMCrudListingActionIcon(lstActIcons[actionIconKey]);

                                                                if (
                                                                    (!!record.deleted_at && actIcon.hideOnDeleted) ||
                                                                    (!record.deleted_at && actIcon.showOnDeletedOnly) ||
                                                                    actIcon.hideOnCondition(record)
                                                                ) {
                                                                    return;
                                                                }

                                                                return (
                                                                    <Tooltip
                                                                        key={'tdAKey-' + trDex + '-' + actIconDex}
                                                                        title={actIcon.getTitle(record)}
                                                                    >
                                                                        <IconButton
                                                                            className={'lz-icon-button-' + actIcon.getColor(record)}
                                                                            onClick={() => handleActionIconClick(actionIconKey, actIcon, record)}
                                                                        >
                                                                            {actIcon.getIcon(record)}
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                );
                                                            })}
                                                        </TableCell>
                                                    )}
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                component="div"
                                page={currentPage || 0}
                                count={totalRecords || 0}
                                onPageChange={(evt, page) => onPageChange(+page)}
                                rowsPerPage={recordsPerPage || 10}
                                onRowsPerPageChange={(evt) => onRecordsPerPageChange(+evt.target.value)}
                            />
                        </React.Fragment>
                    )}
                </Paper>
            )}
        </React.Fragment>
    );
});

export default LzXCrudListing;
